import React, { useState, useEffect, useCallback } from 'react';
import { LanguageSwitcher } from './LanguageSwitcher';
import './EntryCodeModal.css';

interface EntryCodeModalProps {
  isOpen: boolean;
  title?: string; // Made optional and more generic
  displayToken: string;
  onSuccess: (data: any) => void;
  onError: (error: string) => void;
  type?: 'questionnaire' | 'registration'; // Determines endpoint type
  initialLanguage?: 'en' | 'el'; // Language from token/session
}

interface TokenInfo {
  questionnaire_type: string;
  link_expires_at: string;     // Changed field name
  otp_expires_at: string;      // New field for OTP expiration
  failed_attempts: number;     // Changed from entry_attempts
  max_attempts: number;
  locked: boolean;             // New field
  completed: boolean;          // New field
}

const EntryCodeModal: React.FC<EntryCodeModalProps> = ({
  isOpen,
  title,
  displayToken,
  type = 'questionnaire', // Default to questionnaire for backward compatibility
  onSuccess,
  onError,
  initialLanguage
}) => {
  const [otp, setOtp] = useState('');  // Changed from entryCode to otp
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0); // Will be set by validateToken()
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [language, setLanguage] = useState<'en' | 'el'>('el'); // Default to 'el'

  // Update language when initialLanguage prop changes or component mounts
  useEffect(() => {
    const targetLanguage = initialLanguage || 'el';
    setLanguage(targetLanguage);
  }, [initialLanguage]);

  const validateToken = useCallback(async () => {
    try {
      // NEW ARCHITECTURE: Call the appropriate endpoint based on type  
      const endpoint = type === 'registration' ? 
        `/api/patient-registration/${displayToken}` : 
        `/api/questionnaire/${displayToken}`;
      const response = await fetch(endpoint, { credentials: 'include' });
      const result = await response.json();
      
      console.log('🔍 Full link access response:', result);
      
      if (response.ok) {
        const data = result.data;
        
        // Check access type from new architecture
        if (data.access_type === 'form') {
          // User has valid session - can access form directly
          onSuccess(data);
          return;
        } else if (data.access_type === 'otp_required') {
          // OTP required - setup modal
          setTokenInfo(data.token_info);
          
          console.log('📊 Token info:', data.token_info);
          
          // Calculate remaining time based on OTP expiration
          let calculatedTime = 600; // Default 10 minutes (matching backend config)
          
          if (data.token_info.otp_expires_at) {
            const otpExpiresAt = new Date(data.token_info.otp_expires_at);
            const now = new Date();
            
            console.log('⏰ OTP expires at:', otpExpiresAt.toISOString());
            console.log('⏰ Current time:', now.toISOString());
            
            // Check if dates are valid
            if (!isNaN(otpExpiresAt.getTime()) && !isNaN(now.getTime())) {
              const timeDiff = otpExpiresAt.getTime() - now.getTime();
              calculatedTime = Math.max(0, Math.floor(timeDiff / 1000));
              
              console.log('⏰ Time difference (ms):', timeDiff);
              console.log('⏰ Calculated remaining time (s):', calculatedTime);
            } else {
              console.warn('❌ Invalid date received from server, using fallback timer');
            }
          } else {
            console.warn('❌ No otp_expires_at received, using fallback timer');
          }
          
          setTimeRemaining(calculatedTime);
          
          // Check if already expired
          if (calculatedTime <= 0) {
            setError('Ο κωδικός έχει λήξει. Παρακαλώ ζητήστε νέο σύνδεσμο.');
          }
        }
      } else {
        // Handle errors according to new architecture status codes
        if (response.status === 410) {
          onError('Το link έχει λήξει ή έχει ήδη υποβληθεί.');
        } else if (response.status === 423) {
          onError('Για λόγους ασφάλειας το link κλειδώθηκε. Επικοινωνήστε με τον γιατρό.');
        } else {
          onError(result.message || 'Μη έγκυρος σύνδεσμος');
        }
      }
    } catch (error) {
      console.error('❌ Link access error:', error);
      onError('Σφάλμα επικοινωνίας με τον διακομιστή');
    }
  }, [displayToken, onSuccess, onError, type]);

  // Timer for entry code expiration (2 minutes)
  useEffect(() => {
    if (!isOpen || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setError('Ο κωδικός έχει λήξει. Παρακαλώ ζητήστε νέο σύνδεσμο.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, timeRemaining]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp('');  // Changed from setEntryCode
      setError('');
      setIsLoading(false);
      
      // Ensure language is set when modal opens
      if (initialLanguage) {
        setLanguage(initialLanguage);
      }
      
      // Validate token when modal opens - this will set the correct timeRemaining
      validateToken();
    }
  }, [isOpen, displayToken, validateToken, initialLanguage]);

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // NEW ARCHITECTURE: Check for 6-digit OTP instead of 4-digit
    if (otp.length !== 6) {
      setError('Ο κωδικός πρέπει να έχει 6 ψηφία');
      return;
    }

    if (timeRemaining <= 0) {
      setError('Ο κωδικός έχει λήξει. Παρακαλώ ζητήστε νέο σύνδεσμο.');
      return;
    }

    if (tokenInfo && tokenInfo.failed_attempts >= tokenInfo.max_attempts) {
      setError('Έχετε εξαντλήσει τις διαθέσιμες προσπάθειες. Παρακαλώ ζητήστε νέο σύνδεσμο.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // NEW ARCHITECTURE: Call appropriate OTP verification endpoint
      const endpoint = type === 'registration' ? 
        `/api/patient-registration/${displayToken}/verify` : 
        `/api/questionnaire/${displayToken}/otp/verify`;
      const body = { otp: otp };
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (response.ok) {
        // OTP verified successfully, session created
        onSuccess(result.data);
      } else {
        // Handle errors according to new architecture status codes
        let errorMessage = 'Λάθος κωδικός.';
        
        if (response.status === 401) {
          errorMessage = result.detail || 'Λάθος κωδικός.';
        } else if (response.status === 410) {
          errorMessage = 'Το link έχει λήξει ή έχει ήδη υποβληθεί.';
        } else if (response.status === 423) {
          errorMessage = 'Για λόγους ασφάλειας το link κλειδώθηκε. Επικοινωνήστε με τον γιατρό σας για νέο link.';
        } else if (response.status === 429) {
          errorMessage = 'Πάρα πολλές προσπάθειες. Δοκιμάστε αργότερα.';
        }
        
        setError(errorMessage);
        
        // Update failed attempts if token info exists
        if (tokenInfo && (response.status === 401 || response.status === 423)) {
          const newFailedAttempts = tokenInfo.failed_attempts + 1;
          const isLocked = response.status === 423 || newFailedAttempts >= tokenInfo.max_attempts;
          
          setTokenInfo({
            ...tokenInfo,
            failed_attempts: newFailedAttempts,
            locked: isLocked
          });
        }
      }
    } catch (error) {
      setError('Σφάλμα επικοινωνίας με τον διακομιστή');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // NEW ARCHITECTURE: Allow up to 6 digits for OTP
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setOtp(value);
    setError('');
  };

  if (!isOpen) return null;

  // Bilingual content
  const content = {
    en: {
      defaultTitles: {
        registration: 'Patient Registration',
        questionnaire: 'Questionnaire',
        default: 'Code Verification'
      },
      securityCheck: 'Identity Verification',
      instructions: 'Check your email for the 6-digit security code.',
      enterCode: 'Enter the code below:',
      timeRemaining: 'Time remaining:',
      attempts: 'Attempts:',
      verifying: 'Verifying...',
      verifyButton: 'Verify Code',
      language: 'Language'
    },
    el: {
      defaultTitles: {
        registration: 'Εγγραφή Ασθενή',
        questionnaire: 'Ερωτηματολόγιο',
        default: 'Επαλήθευση Κωδικού'
      },
      securityCheck: 'Έλεγχος Ταυτότητας',
      instructions: 'Ελέγξτε το email σας για τον 6-ψήφιο κωδικό ασφαλείας.',
      enterCode: 'Εισάγετε τον κωδικό παρακάτω:',
      timeRemaining: 'Χρόνος που απομένει:',
      attempts: 'Προσπάθειες:',
      verifying: 'Επαλήθευση...',
      verifyButton: 'Επαλήθευση Κωδικού',
      language: 'Γλώσσα'
    }
  };

  const t = content[language];

  const getDefaultTitle = (modalType: 'questionnaire' | 'registration'): string => {
    return t.defaultTitles[modalType] || t.defaultTitles.default;
  };

  const getDisplayTitle = (): string => {
    return title || getDefaultTitle(type);
  };

  return (
    <div className="entry-code-overlay">
      <div className="entry-code-modal">
        <div className="modal-header">
          <div className="form-info">
            <h2><strong>{getDisplayTitle()}</strong></h2>
          </div>
          <LanguageSwitcher
            selectedLanguage={language}
            onLanguageChange={setLanguage}
            variant="default"
            className="entry-code-lang-switcher"
          />
        </div>

        <div className="modal-body">
          <div className="security-notice">
            <div className="security-icon">🔒</div>
            <div className="instructions">
              <p><strong>{t.securityCheck}</strong></p>
              <p>{t.instructions}</p>
              <p className="code-instruction">{t.enterCode}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="entry-code-input-group">
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={handleInputChange}
                placeholder="000000"
                maxLength={6}
                pattern="\d{6}"
                className={`entry-code-input ${error ? 'error' : ''}`}
                disabled={isLoading || timeRemaining <= 0 || (tokenInfo ? (tokenInfo.failed_attempts >= tokenInfo.max_attempts || tokenInfo.locked) : false)}
                autoFocus
              />
            </div>

            <div className="timer-container">
              <div className={`timer ${timeRemaining <= 30 ? 'urgent' : ''}`}>
                ⏱️ {t.timeRemaining} {formatTime(timeRemaining)}
              </div>
              {tokenInfo && (
                <div className={`attempts-info ${(tokenInfo.failed_attempts >= tokenInfo.max_attempts || tokenInfo.locked) ? 'max-reached' : ''}`}>
                  {t.attempts} {tokenInfo.failed_attempts}/{tokenInfo.max_attempts}
                  {(tokenInfo.failed_attempts >= tokenInfo.max_attempts || tokenInfo.locked) && (
                    <span> ⚠️</span>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="submit"
                className="btn-verify"
                disabled={isLoading || otp.length !== 6 || timeRemaining <= 0 || (tokenInfo ? (tokenInfo.failed_attempts >= tokenInfo.max_attempts || tokenInfo.locked) : false)}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                    {t.verifying}
                  </>
                ) : (
                  t.verifyButton
                )}
              </button>
            </div>
          </form>
        </div>


      </div>
    </div>
  );
};

export default EntryCodeModal;